import React, { createContext, useContext, useState, useCallback, useMemo, useEffect, useRef } from "react";
import * as analyticsApi from "features/analytics/studio/api/analyticsApi";

const STORAGE_KEY_PREFIX = "pinnedBoards_";

function getStoredPinnedIds(teamId) {
  if (!teamId || typeof window === "undefined") return [];
  try {
    const raw = localStorage.getItem(STORAGE_KEY_PREFIX + teamId);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed.map(String) : [];
  } catch {
    return [];
  }
}

function setStoredPinnedIds(teamId, ids) {
  if (!teamId || typeof window === "undefined") return;
  try {
    localStorage.setItem(STORAGE_KEY_PREFIX + teamId, JSON.stringify(ids));
  } catch (_) {}
}


// One network call per team per session, however many providers mount.
//
// PinnedBoardsProvider is mounted by alternative layouts (Dashboard,
// AnalyticsLayout) and its effect keys on `teamId`, which
// churns null -> slug -> id while auth settles. On one page load this fired
// GET /boards/pinned three times, each a full round trip. React StrictMode
// doubles it again in development.
//
// Deduping at the fetch rather than restructuring the layouts: it holds no
// matter how many providers exist or how the id transitions, and the sidebar
// genuinely needs this data on every page.
const boardsCache = new Map();   // teamId -> string[]
const boardsInFlight = new Map(); // teamId -> Promise<string[]>

function fetchPinnedOnce(teamId) {
  const key = String(teamId);
  if (boardsCache.has(key)) return Promise.resolve(boardsCache.get(key));
  if (boardsInFlight.has(key)) return boardsInFlight.get(key);

  const p = analyticsApi.fetchPinnedBoards()
    .then((res) => {
      const ids = res?.data?.boardIds;
      const strIds = Array.isArray(ids) ? ids.map(String) : null;
      if (strIds) boardsCache.set(key, strIds);
      return strIds;
    })
    .finally(() => { boardsInFlight.delete(key); });

  boardsInFlight.set(key, p);
  return p;
}

/** Keep the cache honest when this tab changes the pins. */
function primePinnedCache(teamId, ids) {
  if (teamId == null) return;
  boardsCache.set(String(teamId), ids.map(String));
}

const defaultValue = {
  pinnedBoardIds: [],
  togglePin: () => {},
  isPinned: () => false,
  ensureLoaded: () => {},
};

const PinnedBoardsContext = createContext(defaultValue);

export function PinnedBoardsProvider({ teamId, children }) {
  const [pinnedBoardIds, setPinnedBoardIds] = useState(() => getStoredPinnedIds(teamId));

  // NO fetch on mount.
  //
  // The provider is mounted by three layouts that wrap essentially the whole
  // app, so an eager fetch meant every page paid for pinned boards. They are
  // only rendered in Reports. State initialises from localStorage — instant,
  // no network — and the server sync happens when a consumer that actually
  // needs it calls ensureLoaded().
  const loadedRef = useRef(false);
  const ensureLoaded = useCallback(async () => {
    if (!teamId || loadedRef.current) return;
    loadedRef.current = true;
    try {
      const strIds = await fetchPinnedOnce(teamId);
      if (strIds) {
        setPinnedBoardIds(strIds);
        setStoredPinnedIds(teamId, strIds);
      }
    } catch (_) {
      loadedRef.current = false; // let a later mount retry
    }
  }, [teamId]);

  // A team switch invalidates what we loaded.
  useEffect(() => {
    loadedRef.current = false;
    setPinnedBoardIds(getStoredPinnedIds(teamId));
  }, [teamId]);

  const togglePin = useCallback(
    (boardId) => {
      if (!teamId || boardId == null) return;
      const id = String(boardId);
      setPinnedBoardIds((prev) => {
        const next = prev.includes(id) ? prev.filter((b) => b !== id) : [...prev, id];
        setStoredPinnedIds(teamId, next);
        // Keep the session cache in step, or a later mount would restore the
        // pre-toggle list from the cached fetch.
        primePinnedCache(teamId, next);
        // Persist to server (fire-and-forget; localStorage already updated)
        analyticsApi.updatePinnedBoards(next.map((s) => (s === String(parseInt(s, 10)) ? parseInt(s, 10) : s))).catch(() => {});
        return next;
      });
    },
    [teamId]
  );

  const isPinned = useCallback(
    (boardId) => pinnedBoardIds.includes(String(boardId)),
    [pinnedBoardIds]
  );

  const value = useMemo(
    () => ({ pinnedBoardIds, togglePin, isPinned, ensureLoaded }),
    [pinnedBoardIds, togglePin, isPinned, ensureLoaded]
  );

  return (
    <PinnedBoardsContext.Provider value={value}>
      {children}
    </PinnedBoardsContext.Provider>
  );
}

export function usePinnedBoards() {
  return useContext(PinnedBoardsContext);
}

export default PinnedBoardsContext;
